import { ReactNode } from "react";
import { match, P } from "ts-pattern";

import {
  GetSmartphoneFamiliesSlugDocument,
  GetSmartphoneFamiliesSlugQuery,
} from "app/smartphone/[slug]/get-smarthphone-family-slug.query.generated";
import {
  GetSmartphoneFamiliesSlugAndFlagshipDocument,
  GetSmartphoneFamiliesSlugAndFlagshipQuery,
} from "app/smartphone/[slug]/smartphone-families-product-page.query.generated";
import { AppLayout } from "shared/Layout";
import { initializeApollo } from "utils/with-apollo.utils";
import { withApp } from "utils/with-app.utils";
import { withSeo } from "utils/with-seo.utils";

import {
  getStaticProps as CatalogStaticProps,
  Smartphones,
  SmartphonesProps,
} from "../page";

import {
  getStaticProps as ProductStaticProps,
  ProductPage,
  ProductPageProps,
} from "./[reference]/page";
import {
  GetCatalogTagsDocument,
  GetCatalogTagsQuery,
} from "./catalog-page-tags.query.generated";

type CatalogQueryResult = { data: GetCatalogTagsQuery };
type ProductQueryResult = { data: GetSmartphoneFamiliesSlugQuery };
type FamilySlugAndFlagship = {
  data: GetSmartphoneFamiliesSlugAndFlagshipQuery;
};

type GetStaticPropsParams = {
  params: { slug?: string };
};

export const SlugComponent = (props: SmartphonesProps | ProductPageProps) =>
  match(props)
    .with({ productData: P.not(P.nullish) }, (productProps) => (
      <ProductPage {...productProps} />
    ))
    .with({ catalogData: P.not(P.nullish) }, (tagProps) => (
      <Smartphones {...tagProps} />
    ))
    .otherwise(() => null);

SlugComponent.getLayout = (page: ReactNode, pageProps: ProductPageProps) => (
  <>
    <AppLayout withBanner {...pageProps}>
      {page}
    </AppLayout>

    <div className="h-24" />
  </>
);

export const getStaticPaths = async () => {
  const apolloClient = initializeApollo();
  const { data: catalogTagData }: CatalogQueryResult = await apolloClient.query(
    {
      query: GetCatalogTagsDocument,
      fetchPolicy: "network-only",
    },
  );

  const { data: getSmartphoneFamiliesSlug }: ProductQueryResult =
    await apolloClient.query({
      query: GetSmartphoneFamiliesSlugDocument,
      fetchPolicy: "network-only",
    });

  const tagSlugs = (catalogTagData?.tags?.data ?? []).map(
    ({ attributes }) => attributes?.slug,
  );

  const invalidTagSlugs = tagSlugs.filter((s) => typeof s !== "string");
  if (invalidTagSlugs.length > 0) {
    throw new Error(
      `getStaticPaths(/smartphone/[slug]) - invalid tag slug(s) from GetCatalogTagsDocument: ${JSON.stringify(
        invalidTagSlugs,
      )}`,
    );
  }

  const paths = (tagSlugs as string[]).map((slug) => ({ params: { slug } }));

  // Build family flagship paths and ensure slug is a string
  const familyEntries =
    getSmartphoneFamiliesSlug?.b2CSmartphoneCatalog?.data?.attributes
      ?.entries ?? [];

  const familySlugs = familyEntries.map(
    (e) =>
      e?.flagship?.data?.attributes?.smartphoneVariant?.data?.attributes
        ?.smartphoneFamily?.data?.attributes?.slug,
  );

  const invalidFamilyIndexes = familySlugs
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => typeof s !== "string")
    .map(({ i }) => i);

  if (invalidFamilyIndexes.length > 0) {
    const problematic = invalidFamilyIndexes.map((i) => ({
      index: i,
      entry: familyEntries[i],
      slugValue: familySlugs[i],
    }));
    throw new Error(
      `getStaticPaths(/smartphone/[slug]) - invalid family slug(s) from GetSmartphoneFamiliesSlugDocument: ${JSON.stringify(
        problematic,
        null,
        2,
      )}`,
    );
  }

  const families = familySlugs.map((slug) => ({
    params: { slug },
  }));

  return {
    paths: [...(paths || []), ...(families || [])],
    fallback: "blocking",
  };
};

export const getStaticProps = async ({
  params: { slug: tagOrfamilySlug },
}: GetStaticPropsParams) => {
  const apolloClient = initializeApollo();
  const { data: catalogTagData }: CatalogQueryResult = await apolloClient.query(
    {
      query: GetCatalogTagsDocument,
      fetchPolicy: "network-only",
    },
  );

  const { data: getSmartphoneFamiliesSlug }: FamilySlugAndFlagship =
    await apolloClient.query({
      query: GetSmartphoneFamiliesSlugAndFlagshipDocument,
      fetchPolicy: "network-only",
    });

  const tagsSlug = catalogTagData?.tags?.data?.map(
    ({ attributes }) => attributes?.slug,
  );

  const catalog =
    getSmartphoneFamiliesSlug?.b2CSmartphoneCatalog?.data?.attributes?.entries?.map(
      (catalogEntry) => {
        const flagship = catalogEntry?.flagship?.data;

        const familySlug =
          flagship?.attributes?.smartphoneVariant?.data?.attributes
            ?.smartphoneFamily?.data?.attributes?.slug;

        const isFlagshipInStock =
          flagship?.attributes?.subscriptionItem?.stock?.isOnline;

        const variantInStock = catalogEntry?.subscriptions?.data?.find(
          (subscription) =>
            subscription?.attributes?.subscriptionItem?.stock?.isOnline,
        );

        const selectedVariantSlug =
          isFlagshipInStock || !variantInStock
            ? flagship?.attributes?.smartphoneVariant?.data?.attributes?.slug
            : variantInStock?.attributes?.smartphoneVariant?.data?.attributes
                ?.slug;

        return {
          familySlug,
          variantSlug: selectedVariantSlug,
        };
      },
    );

  if (
    [
      ...(tagsSlug ?? []),
      "legacy",
      "meilleures-ventes",
      "prochainement",
    ]?.includes(tagOrfamilySlug)
  ) {
    return CatalogStaticProps({ params: { slug: tagOrfamilySlug } });
  }

  const flagshipInfo = catalog?.find(
    ({ familySlug }) => familySlug === tagOrfamilySlug,
  );

  if (flagshipInfo && flagshipInfo.variantSlug && flagshipInfo.familySlug) {
    return ProductStaticProps({
      params: {
        slug: flagshipInfo.familySlug,
        reference: flagshipInfo.variantSlug,
      },
    });
  } else {
    // No flashship, redirect to catalog
    return {
      redirect: {
        destination: "/smartphone",
      },
    };
  }
};

export default withApp(SlugComponent, [withSeo]);
